import React, { CSSProperties, useState } from "react"
import Box from "@mui/material/Box"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import { activeAnnotationSessionAtom, annotationGroupsAtom, eventReviewSourceGroupAtom } from "../../../Atoms/Annotations"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { useAnnotationService } from "../../../../../../../Hooks/useAnnotationService"
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md"
import { useModalProvider } from "../../../../../../../Providers/ModalProvider"
import { Scrollbar } from "../../../../../../../Constants/StyledComponents"
import { ModalTitleAndCloseButton } from "../ModalTitleAndCloseButton"
import { DefaultAnnotationSessionSelector, SessionSelectorChangeEvent } from "./DefaultAnnotationGroupSelector"
import { AnnotationGroup } from "../../../Types/Annotations"
import { MobergIconSize } from "../../../../../../../Components/MobergIcon/MobergIcon"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../../../../../../Components/MobergButton/MobergButton"
import { MobergTheme, getColors } from "../../../../../../../Components/MobergThemes/MobergColors"
import { MobergFontSize } from "../../../../../../../Components/MobergFont/MobergFont"
import { currentPatientFileInfoAtom } from "../../../Atoms/PatientFile"
import { DataSource } from "../../../Types/DataSource"
import { useEnvironmentVariablesProvider } from "../../../../../../../Providers/EnvironmentVariablesProvider"
import { useAuthProvider } from "../../../../../../../Providers/AuthProvider"
import { useDataReviewPermissions } from "../../../../../../../Hooks/useDataReviewPermissions"

type ReviewMode = "retrospective" | "event-review"

type AnnotationGroupLoaderProps = {
	style?: CSSProperties
	defaultMode?: ReviewMode
	excludeReadOnlyGroups?: boolean
}

export const AnnotationGroupLoader = ({ style, defaultMode = "retrospective", excludeReadOnlyGroups = false }: AnnotationGroupLoaderProps) => {
	const { close } = useModalProvider()
	const { createAnnotationGroup, loadAnnotationGroups, getAnnotationGroups } = useAnnotationService()
	const { dataSourceMap } = useRecoilValue(currentPatientFileInfoAtom)
	const setActiveAnnotationSession = useSetRecoilState(activeAnnotationSessionAtom)
	const setEventReviewSourceGroup = useSetRecoilState(eventReviewSourceGroupAtom)
	const annotationSessions = useRecoilValue(annotationGroupsAtom)
	const { environmentVariables } = useEnvironmentVariablesProvider()
	const authProvider = useAuthProvider()
	const { canManageDataReview } = useDataReviewPermissions()

	const showModeSelector = environmentVariables.FEATURE_FLAG_EVENT_REVIEW
	const [selectedMode, setSelectedMode] = useState<ReviewMode>(showModeSelector ? defaultMode : "retrospective")

	// --- Retrospective mode state ---
	const [validationError, setValidationError] = useState<string>()
	const [selectedAnnotationGroupIds, setSelectedAnnotationGroupIds] = useState(new Set<string>())
	const [isFetching, setIsFetching] = useState(false)
	const [newSessionName, setNewSessionName] = useState("")
	const [initialAnnotationSession, setInitialAnnotationSession] = useState<AnnotationGroup>()
	const [sessionSelectorEvent, setSessionSelectorEvent] = useState<SessionSelectorChangeEvent>({ type: "existing", sessionId: "", sessionName: "" })
	const annotationSessionsForLoading = excludeReadOnlyGroups
		? annotationSessions.filter(session => !session.read_only)
		: annotationSessions
	const shouldCreateAnnotationSession = sessionSelectorEvent.type === "create"
	const numAnnotationsToLoad =
		[...selectedAnnotationGroupIds].map(id => annotationSessionsForLoading.find(g => g.id.toString() === id.toString())?.nannotations ?? 0).reduce((a, b) => a + b, 0) +
		(initialAnnotationSession && !selectedAnnotationGroupIds.has(initialAnnotationSession.id.toString()) ? initialAnnotationSession.nannotations : 0)

	// --- Event review mode state ---
	const sourceGroupCandidates = annotationSessions.filter(group => group.nannotations > 0 && !group.read_only)
	const targetGroupName = `Review Notes - ${authProvider.currentUser.email}`
	const existingTargetGroup = annotationSessions.find(g => g.group_name === targetGroupName && !g.read_only)
	const [selectedSourceGroupId, setSelectedSourceGroupId] = useState<string>(
		sourceGroupCandidates.length > 0 ? sourceGroupCandidates[0].id.toString() : ""
	)
	const [selectedTargetGroupId, setSelectedTargetGroupId] = useState<string>(
		existingTargetGroup ? existingTargetGroup.id.toString() : "__auto_create__"
	)
	const [eventReviewError, setEventReviewError] = useState<string>()

	const eventReviewTabDisabled = sourceGroupCandidates.length === 0

	const isLoadButtonDisabled =
		isFetching ||
		(selectedAnnotationGroupIds.size === 0 && !initialAnnotationSession && !shouldCreateAnnotationSession) ||
		(shouldCreateAnnotationSession && !canManageDataReview)
	const tabStyles = {
		textTransform: "none",
		fontSize: MobergFontSize.REGULAR,
		fontWeight: 600,
		color: "var(--text-secondary-color)",
		"&.Mui-selected": {
			color: "var(--color-accent-interactive)",
		},
		"&.Mui-disabled": {
			color: "var(--text-secondary-color)",
			opacity: 0.5,
		},
	}

	const toggleAnnotationGroup = (annotationGroupId: string | number) => {
		setSelectedAnnotationGroupIds(set => {
			const copy = new Set(set)

			if (copy.has(annotationGroupId.toString())) {
				copy.delete(annotationGroupId.toString())
			} else {
				copy.add(annotationGroupId.toString())
			}

			return copy
		})
	}

	const formatAnnotationLabel = (numAnnotations: number) => {
		return numAnnotations === 1 ? "annotation" : "annotations"
	}

	const formIsValid = () => {
		// The session already exists. No validation needed.
		if (initialAnnotationSession || !shouldCreateAnnotationSession) {
			return true
		}

		let error = undefined

		const cleaned = newSessionName.trim()

		if (!cleaned || cleaned === "") {
			error = "Session name cannot be empty"
		}

		// This is dumb because there's no reason why two annotation sessions can't have the same name.
		// We're handling it because the backend throws an error and it's nicer if we handle it on the frontend
		// instead of just alerting the error message.
		const foundSameName = annotationSessions.find(session => session.group_name === newSessionName)

		if (foundSameName) {
			error = "Session already exists with the same name."
		}

		if (error) {
			setValidationError(error)
			return false
		}

		return true
	}

	const handleLoad = async () => {
		if (!formIsValid()) {
			return
		}

		setIsFetching(true)

		let annotationGroupId: string | undefined = undefined

		if (shouldCreateAnnotationSession) {
			const annotationGroup = await createAnnotationGroup(newSessionName, dataSourceMap.get(DataSource.CURRENT_PATIENT) as number)

			if (!annotationGroup) {
				return
			}

			annotationGroupId = annotationGroup.id.toString()
		}

		const idsToLoad = [...selectedAnnotationGroupIds]

		if (initialAnnotationSession) {
			idsToLoad.push(initialAnnotationSession.id.toString())
		}

		loadAnnotationGroups(idsToLoad)
			.then(() => {
				if (initialAnnotationSession) {
					setActiveAnnotationSession(initialAnnotationSession)
				} else {
					getAnnotationGroups().then((groups: AnnotationGroup[]) => {
						const foundAnnotationSession = groups.find(session => session.id.toString() === annotationGroupId)
						if (foundAnnotationSession) {
							setActiveAnnotationSession(foundAnnotationSession)
						}
					})
				}
			})
			.then(close)
			.catch(err => alert(err))
			.finally(() => setIsFetching(false))
	}

	const handleEventReviewLoad = async () => {
		if (!selectedSourceGroupId) {
			setEventReviewError("Please select a source group.")
			return
		}

		const resolvedTargetId = selectedTargetGroupId
		const sourceGroup = annotationSessions.find(g => g.id.toString() === selectedSourceGroupId)

		if (!sourceGroup) {
			setEventReviewError("Selected source group not found.")
			return
		}

		if (resolvedTargetId !== "__auto_create__" && resolvedTargetId === selectedSourceGroupId) {
			setEventReviewError("Source and target groups cannot be the same.")
			return
		}

		setEventReviewError(undefined)
		setIsFetching(true)

		try {
			let targetGroup: AnnotationGroup

			if (resolvedTargetId === "__auto_create__") {
				const existing = annotationSessions.find(g => g.group_name === targetGroupName && !g.read_only)
				if (existing) {
					targetGroup = existing
				} else {
					const created = await createAnnotationGroup(targetGroupName, dataSourceMap.get(DataSource.CURRENT_PATIENT) as number)
					if (!created) {
						setIsFetching(false)
						return
					}
					targetGroup = created
				}
			} else {
				const found = annotationSessions.find(g => g.id.toString() === resolvedTargetId && !g.read_only)
				if (!found) {
					setEventReviewError("Target group could not be resolved.")
					setIsFetching(false)
					return
				}
				targetGroup = found
			}

			if (sourceGroup.id.toString() === targetGroup.id.toString()) {
				setEventReviewError("Source and target groups cannot be the same.")
				setIsFetching(false)
				return
			}

			const groups = await getAnnotationGroups()
			const freshTarget = groups.find((g: AnnotationGroup) => g.id.toString() === targetGroup.id.toString())
			if (freshTarget) {
				targetGroup = freshTarget
			}

			await loadAnnotationGroups([sourceGroup.id.toString(), targetGroup.id.toString()])

			setActiveAnnotationSession(targetGroup)
			setEventReviewSourceGroup(sourceGroup)
			close()
		} catch (err) {
			alert(err)
		} finally {
			setIsFetching(false)
		}
	}

	const handleAnnotationSessionChanged = (event: SessionSelectorChangeEvent) => {
		setSessionSelectorEvent(event)

		if (validationError) {
			setValidationError(undefined)
		}

		switch (event.type) {
			case "create":
				setInitialAnnotationSession(undefined)
				setNewSessionName(event.sessionName)
				break
			case "existing":
				setInitialAnnotationSession(annotationSessionsForLoading.find(session => session.id.toString() === event.sessionId.toString()))
				break
		}
	}

	const handleToggleAnnotationGroup = (group: AnnotationGroup) => {
		if (initialAnnotationSession && group.id.toString() === initialAnnotationSession.id.toString()) {
			return
		}

		toggleAnnotationGroup(group.id)
	}

	const getCheckboxColor = (group: AnnotationGroup) => {
		if (group.id.toString() === initialAnnotationSession?.id.toString()) {
			return "var(--color-chart-nav-disabled)"
		}

		return "var(--color-accent-interactive)"
	}

	const getTextColor = (group: AnnotationGroup) => {
		if (group.id.toString() === initialAnnotationSession?.id.toString()) {
			return "var(--text-secondary-color)"
		}

		return "var(--color-text-primary)"
	}

	const getCursor = (group: AnnotationGroup) => {
		if (group.id.toString() === initialAnnotationSession?.id.toString()) {
			return "default"
		}

		return "pointer"
	}

	const targetGroupOptions: Array<{ label: string; value: string }> = [
		{ label: targetGroupName, value: "__auto_create__" },
		...annotationSessions
			.filter(g => g.group_name !== targetGroupName && !g.read_only)
			.map(g => ({ label: g.group_name, value: g.id.toString() })),
	]

	const isEventReviewLoadDisabled = isFetching || !selectedSourceGroupId || sourceGroupCandidates.length === 0 || !canManageDataReview

	return (
		<div style={{ display: "flex", flexDirection: "column", padding: "5px", height: "100%", position: "relative", minWidth: "550px", color: "var(--color-text-primary)", ...style }}>
			<ModalTitleAndCloseButton title={"Annotation Setup"} closeFunction={close} />

			{showModeSelector && (
				<Box sx={{ display: "flex", justifyContent: "center", padding: "0 20px", marginTop: "8px" }}>
					<Tabs
						value={selectedMode}
						onChange={(_, value: ReviewMode) => setSelectedMode(value)}
						sx={{
							minHeight: "40px",
							"& .MuiTabs-indicator": {
								backgroundColor: "var(--color-accent-interactive)",
							},
						}}
					>
						<Tab
							label="Retrospective Review"
							value="retrospective"
							sx={tabStyles}
							disableRipple
						/>
						<Tab
							label="Event Review"
							value="event-review"
							disabled={eventReviewTabDisabled}
							title={eventReviewTabDisabled ? "No annotation groups available" : undefined}
							sx={tabStyles}
							disableRipple
						/>
					</Tabs>
				</Box>
			)}

			{selectedMode === "retrospective" && (
				<>
					<div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "20px", overflowY: "scroll", minHeight: 0 }}>
						<DefaultAnnotationSessionSelector onChange={handleAnnotationSessionChanged} annotationSessions={annotationSessionsForLoading} validationError={validationError} />

						<div style={{ fontWeight: "bold", padding: "10px 0" }}> Load annotations from other sessions: </div>

						<Scrollbar style={{ flex: 1, overflowY: "scroll", minHeight: 0 }}>
							<div style={{ padding: "10px", paddingTop: 0 }}>
								{annotationSessionsForLoading.map(group => {
									return (
										<div
											key={group.id.toString()}
											style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 0", fontSize: "16px", cursor: getCursor(group), borderBottom: "1px solid var(--color-border-default)" }}
											onClick={() => handleToggleAnnotationGroup(group)}
										>
											<div>
												{selectedAnnotationGroupIds.has(group.id.toString()) || initialAnnotationSession?.id.toString() === group.id.toString() ? (
													<MdCheckBox color={getCheckboxColor(group)} size={MobergIconSize.REGULAR} />
												) : (
													<MdCheckBoxOutlineBlank size={MobergIconSize.REGULAR} />
												)}
											</div>

											<span style={{ cursor: getCursor(group), color: getTextColor(group) }}>
												{group.group_name} ({group.nannotations} {formatAnnotationLabel(group.nannotations)})
											</span>
										</div>
									)
								})}
							</div>
						</Scrollbar>
					</div>

					<div style={{ flex: 0, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", padding: "10px 20px" }}>
						<div>
							{!isLoadButtonDisabled && (
								<div>
									This will load <span style={{ fontWeight: "bold" }}> {numAnnotationsToLoad}</span> {formatAnnotationLabel(numAnnotationsToLoad)}
								</div>
							)}
						</div>

						<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
							{isFetching && (
								<div>Please wait...</div>
							)}

							<MobergButton
								onClick={handleLoad}
								disabled={isLoadButtonDisabled}
								theme={MobergTheme.BLUE}
								variant={MobergButtonVariant.FILLED}
								fontSize={MobergFontSize.LARGE}
								shape={MobergButtonShape.WIDE}>
								{shouldCreateAnnotationSession && !isFetching ? "Create" : "Load"}
							</MobergButton>
						</div>
					</div>
				</>
			)}

			{selectedMode === "event-review" && (
				<>
					<div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "20px", gap: "20px", minHeight: 0 }}>
						<div>
							<div style={{ fontWeight: "bold", paddingBottom: "8px" }}>Review events from:</div>
							<select
								value={selectedSourceGroupId}
								onChange={e => {
									setSelectedSourceGroupId(e.target.value)
									setEventReviewError(undefined)
								}}
								style={{ width: "100%", padding: "10px", border: "1px solid var(--color-border-default)", borderRadius: "6px", cursor: "pointer", height: "41px", background: "var(--color-input-bg)", color: "var(--color-text-primary)" }}
							>
								{sourceGroupCandidates.length === 0 && (
									<option value="" disabled>No groups with annotations</option>
								)}
								{sourceGroupCandidates.map(group => (
									<option key={group.id.toString()} value={group.id.toString()}>
										{group.group_name} ({group.nannotations} {formatAnnotationLabel(group.nannotations)})
									</option>
								))}
							</select>
						</div>

						<div>
							<div style={{ fontWeight: "bold", paddingBottom: "8px" }}>Write annotations to:</div>
							<select
								value={selectedTargetGroupId}
								onChange={e => {
									setSelectedTargetGroupId(e.target.value)
									setEventReviewError(undefined)
								}}
								style={{ width: "100%", padding: "10px", border: "1px solid var(--color-border-default)", borderRadius: "6px", cursor: "pointer", height: "41px", background: "var(--color-input-bg)", color: "var(--color-text-primary)" }}
							>
								{targetGroupOptions.map(opt => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
						</div>

						{eventReviewError && (
							<div style={{ color: getColors(MobergTheme.RED).main, fontSize: MobergFontSize.REGULAR }}>
								{eventReviewError}
							</div>
						)}
					</div>

					<div style={{ flex: 0, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", padding: "10px 20px" }}>
						{isFetching && (
							<div>Please wait...</div>
						)}

						<MobergButton
							onClick={handleEventReviewLoad}
							disabled={isEventReviewLoadDisabled}
							theme={MobergTheme.BLUE}
							variant={MobergButtonVariant.FILLED}
							fontSize={MobergFontSize.LARGE}
							shape={MobergButtonShape.WIDE}
						>
							Load
						</MobergButton>
					</div>
				</>
			)}
		</div>
	)
}
